import http from '@/api/http';
import { ToastService } from '@/services/toast.service';
import {
  ChatRequest,
  ChatResponse,
  Conversation,
  ChatMessage,
  UsageLimits,
  KnowledgeBaseItem,
} from '../interfaces/ai.interface';

export class AiApi {
  private basePath = '/ai';
  private v2Path = '/ai/v2';

  // ==================== CHAT ====================

  async sendMessage(request: ChatRequest): Promise<ChatResponse> {
    try {
      const response = await http.post<any>(`${this.v2Path}/chat`, request);
      return response.data?.data?.data || response.data?.data || response.data;
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Eroare la trimiterea mesajului';
      ToastService.error(errorMessage);
      throw error;
    }
  }

  // ==================== CONVERSATIONS ====================

  async getConversations(): Promise<Conversation[]> {
    try {
      const response = await http.get<any>(`${this.basePath}/conversations`);
      const result = response.data?.data?.data || response.data?.data || [];
      return Array.isArray(result) ? result : [];
    } catch (error: any) {
      ToastService.error('Eroare la încărcarea conversațiilor');
      throw error;
    }
  }

  async getConversationMessages(conversationId: string): Promise<ChatMessage[]> {
    try {
      const response = await http.get<any>(`${this.basePath}/conversations/${conversationId}/messages`);
      const result = response.data?.data?.data || response.data?.data || [];
      return Array.isArray(result) ? result : [];
    } catch (error: any) {
      ToastService.error('Eroare la încărcarea mesajelor');
      throw error;
    }
  }

  async renameConversation(conversationId: string, title: string): Promise<{ id: number; uuid: string; title: string }> {
    try {
      const response = await http.patch<any>(`${this.basePath}/conversations/${conversationId}`, { title });
      return response.data?.data?.data || response.data?.data || response.data;
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Eroare la redenumirea conversației';
      ToastService.error(errorMessage);
      throw error;
    }
  }

  async deleteConversation(conversationId: string): Promise<void> {
    try {
      await http.delete(`${this.basePath}/conversations/${conversationId}`);
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Eroare la ștergerea conversației';
      ToastService.error(errorMessage);
      throw error;
    }
  }

  // ==================== USAGE LIMITS ====================

  async getUsageLimits(): Promise<UsageLimits> {
    try {
      const response = await http.get<any>(`${this.basePath}/usage-limits`);
      return response.data?.data?.data || response.data?.data || {};
    } catch (error: any) {
      ToastService.error('Eroare la încărcarea limitelor de utilizare');
      throw error;
    }
  }

  // ==================== KNOWLEDGE BASE ====================

  async getKnowledgeBase(type?: string): Promise<KnowledgeBaseItem[]> {
    try {
      const url = type
        ? `${this.basePath}/knowledge-base?type=${type}`
        : `${this.basePath}/knowledge-base`;
      const response = await http.get<any>(url);
      const result = response.data?.data?.data || response.data?.data || [];
      return Array.isArray(result) ? result : [];
    } catch (error: any) {
      ToastService.error('Eroare la încărcarea bazei de cunoștințe');
      throw error;
    }
  }
}

export const aiApi = new AiApi();
